import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaBars, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";

const Navbar = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [visaOpen, setVisaOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileVisaOpen, setMobileVisaOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setVisaOpen(false);
    setMobileServicesOpen(false);
    setMobileVisaOpen(false);
  }, [location.pathname]);

  // click outside to close desktop dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
        setVisaOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  const handleNavClick = (path) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
    }
    setMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  // 🔗 Keep these paths aligned with App.js
  const serviceLinks = [
    { to: "/services/education-consulting", label: "Education Consulting" },
    { to: "/services/visa-assistance", label: "Visa Assistance" },
    { to: "/services/oshc", label: "Health Insurance (OSHC / OVHC)" },
    { to: "/services/professional-year", label: "Professional Year Program" },
    { to: "/services/skills-assessment", label: "Skills Assessment" },
  ];

  const visaLinks = [

    { to: "/visa/student-visa-500", label: "Student Visa (Subclass 500)" },
    { to: "/visa/post-study-work-visa-485", label: "Post-Study Work (Subclass 485)" },
    { to: "/visa/tourist-visa-600", label: "Tourist / Visitor (Subclass 600)" },
    { to: "/visa/skilled-189-190-491", label: "Skilled Migration (189 / 190 / 491)" },         // NEW
    { to: "/visa/partner-citizenship", label: "Partner Visa / Citizenship" },                  // NEW
  ];

  return (
    <nav className="bg-gradient-to-r from-gray-200 to-gray-300 shadow-md sticky top-0 z-50 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
        {/* Logo - UPDATED SIZE */}
        <div
          onClick={() => handleNavClick("/")}
          className="flex items-center gap-3 cursor-pointer group"
        >
          <img
            src={logo}
            alt="Glide Logo"
            className="w-28 h-28 md:w-32 md:h-32 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="text-left leading-tight">
            <span className="text-base md:text-lg font-bold text-blue-700 block">
              
            </span>
            <span className="text-xs md:text-sm text-gray-600 font-medium block">
            </span>
          </div>
        </div>

        {/* Desktop Menu */}
        <ul className="hidden md:flex space-x-2 items-center font-medium text-gray-800" ref={dropdownRef}>
          <li>
            <button
              onClick={() => handleNavClick("/")}
              className={`px-3 py-2 rounded-lg hover:text-blue-600 hover:bg-white/60 transition ${
                isActive("/") ? "border-b-2 border-blue-600 font-semibold" : ""
              }`}
            >
              Home
            </button>
          </li>

          <li>
            <button
              onClick={() => handleNavClick("/about")}
              className={`px-3 py-2 rounded-lg hover:text-blue-600 hover:bg-white/60 transition ${
                isActive("/about") ? "border-b-2 border-blue-600 font-semibold" : ""
              }`}
            >
              About
            </button>
          </li>

          {/* Services: link + dropdown */}
          <li
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <div className="flex items-center gap-1">
              <NavLink
                to="/services"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg transition ${
                    isActive
                      ? "border-b-2 border-blue-600 font-semibold"
                      : "hover:text-blue-600 hover:bg-white/60"
                  }`
                }
              >
                Services
              </NavLink>
              <button
                aria-label="Open services menu"
                className="px-2 py-2 rounded-lg hover:bg-white/60"
                onClick={() => setServicesOpen((v) => !v)}
              >
                <FaChevronDown size={12} />
              </button>
            </div>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute left-0 mt-2 w-80 rounded-md border bg-white shadow-xl z-50"
                >
                  <ul className="py-2 text-sm">
                    {serviceLinks.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className="block px-3 py-2 hover:bg-blue-50 rounded"
                          onClick={() => setServicesOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </li>

          {/* Visa dropdown: label links to overview, chevron toggles */}
          <li
            className="relative"
            onMouseEnter={() => setVisaOpen(true)}
            onMouseLeave={() => setVisaOpen(false)}
          >
            <div className="flex items-center gap-1">
              <Link
                to="/services/visa-assistance"
                className="px-3 py-2 rounded-lg hover:text-blue-600 hover:bg-white/60 transition"
                onClick={() => setVisaOpen(false)}
              >
                Visa
              </Link>
              <button
                className="px-2 py-2 rounded-lg hover:bg-white/60"
                onClick={() => setVisaOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={visaOpen}
              >
                <FaChevronDown size={12} />
              </button>
            </div>

            <AnimatePresence>
              {visaOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute left-0 mt-2 w-[32rem] rounded-md border bg-white shadow-xl z-50"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2 text-sm">
                    {visaLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="px-3 py-2 rounded hover:bg-blue-50"
                        onClick={() => setVisaOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </li>

          <li>
            <button
              onClick={() => handleNavClick("/news")}
              className={`px-3 py-2 rounded-lg hover:text-blue-600 hover:bg-white/60 transition ${
                isActive("/news") ? "border-b-2 border-blue-600 font-semibold" : ""
              }`}
            >
              News / Blog
            </button>
          </li>

          <li>
            <button
              onClick={() => handleNavClick("/contact")}
              className={`px-3 py-2 rounded-lg hover:text-blue-600 hover:bg-white/60 transition ${
                isActive("/contact") ? "border-b-2 border-blue-600 font-semibold" : ""
              }`}
            >
              Contact
            </button>
          </li>

          {/* CTA */}
          <li>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="ml-2 bg-gradient-to-r from-blue-600 to-indigo-500 text-white px-4 py-2 rounded-lg shadow font-semibold"
              >
                Book Consultation
              </motion.button>
            </Link>
          </li>
        </ul>

        {/* Mobile Toggle */}
        <div className="md:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white px-6 py-4 space-y-4 shadow-xl border-b rounded-b-lg"
          >
            <li><button onClick={() => handleNavClick("/")} className="text-blue-700 font-medium">Home</button></li>
            <li><button onClick={() => handleNavClick("/about")} className="text-blue-700 font-medium">About</button></li>

            {/* Mobile Services */}
            <li>
              <button
                onClick={() => setMobileServicesOpen((v) => !v)}
                className="w-full flex justify-between items-center text-gray-700 font-semibold uppercase text-sm"
              >
                Services
                <FaChevronDown size={12} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileServicesOpen && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="pl-4 mt-2 space-y-2"
                  >
                    {serviceLinks.map((item) => (
                      <li key={item.to}>
                        <Link to={item.to} onClick={() => setMenuOpen(false)} className="text-blue-700">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>

            {/* Mobile Visa */}
            <li>
              <button
                onClick={() => setMobileVisaOpen((v) => !v)}
                className="w-full flex justify-between items-center text-gray-700 font-semibold uppercase text-sm"
              >
                Visa
                <FaChevronDown size={12} className={`transition-transform ${mobileVisaOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileVisaOpen && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="pl-4 mt-2 space-y-2"
                  >
                    {visaLinks.map((item) => (
                      <li key={item.to}>
                        <Link to={item.to} onClick={() => setMenuOpen(false)} className="text-blue-700">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>

            <li><button onClick={() => handleNavClick("/news")} className="text-blue-700 font-medium">News / Blog</button></li>
            <li><button onClick={() => handleNavClick("/contact")} className="text-blue-700 font-medium">Contact</button></li>
          </motion.ul>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;